/**
 * O status da campanha: os valores, os rótulos, o grafo, os atos e a edição por status (D-189; P-01 a P-10 do plano do
 * pacote Campanhas + DV360; DS-656).
 *
 * ## Uma fonte, três espelhos
 *
 * O MAPA de rótulos e o GRAFO moram aqui. O banco tem os espelhos (`fn_rotulo_status_campanha` e o gatilho
 * `campanha_estado_valido`, 0197), o contrato reexporta o mapa para a tela, e um teste de paridade confere o banco
 * contra este arquivo: rótulo por rótulo e aresta por aresta.
 *
 * ## Os valores gravados não mudam de nome
 *
 * 'Em veiculação' continua gravado e aparece como "Em andamento"; 'Encerrada' continua gravada e aparece como
 * "Concluída" (P-01). Entra a 'Pausada'. Por isso toda frase que cita status passa pelo mapa: o valor gravado nunca
 * chega ao texto.
 *
 * ## Atos, e não um campo
 *
 * O status muda só pelos atos nomeados (Iniciar, Pausar, Retomar, Concluir, Cancelar, Reabrir conclusão) e pelo job
 * de abertura, que é o Iniciar automático de quem já chegou à data (D-189: só o job abre sozinho; o ato manual
 * continua). Aqui moram a decisão de cada ato e a régua de edição por status (§5.5); quem trava, grava e registra a
 * trilha é a API.
 */
import { antesDe, dataNominal, depoisDe } from '../data/data-nominal.js'

/** Os valores GRAVADOS de `campanha.status` (P-01). */
export const STATUS_GRAVADOS_DA_CAMPANHA = ['Programada', 'Em veiculação', 'Pausada', 'Encerrada', 'Cancelada'] as const
export type StatusGravadoDaCampanha = (typeof STATUS_GRAVADOS_DA_CAMPANHA)[number]

export function ehStatusGravadoDaCampanha(valor: string): valor is StatusGravadoDaCampanha {
  return (STATUS_GRAVADOS_DA_CAMPANHA as readonly string[]).includes(valor)
}

/** O mapa ÚNICO de rótulos de tela (P-01, D-189): vale na ficha, na lista, nos filtros, na busca, no CSV e nas frases. */
export const ROTULO_DO_STATUS_DA_CAMPANHA: Readonly<Record<StatusGravadoDaCampanha, string>> = {
  Programada: 'Programada',
  'Em veiculação': 'Em andamento',
  Pausada: 'Pausada',
  Encerrada: 'Concluída',
  Cancelada: 'Cancelada',
}

/**
 * O rótulo de um status gravado. O valor fora do vocabulário volta como veio: nunca um rótulo inventado, e nunca uma
 * exceção no meio de uma frase de recusa.
 */
export function rotuloDoStatusDaCampanha(status: string): string {
  return ehStatusGravadoDaCampanha(status) ? ROTULO_DO_STATUS_DA_CAMPANHA[status] : status
}

/** O rótulo dentro de uma frase: "uma campanha em andamento", "na campanha concluída". */
export function rotuloDoStatusNaFrase(status: string): string {
  return rotuloDoStatusDaCampanha(status).toLowerCase()
}

/**
 * O status na resposta para a tela da versão anterior (DS-657): ela só conhece quatro valores, e a Pausada viaja como
 * 'Em veiculação' SÓ nesse campo, como a coluna antiga do portal (P-11). O valor gravado vai inteiro no campo novo.
 */
export function statusParaATelaAnterior(status: StatusGravadoDaCampanha): Exclude<StatusGravadoDaCampanha, 'Pausada'> {
  return status === 'Pausada' ? 'Em veiculação' : status
}

/** O vocabulário da coluna antiga do portal (P-11): a Pausada é 'Veiculando' só nela. A Cancelada não vai ao portal. */
export const STATUS_DA_CAMPANHA_NO_PORTAL: Readonly<Record<Exclude<StatusGravadoDaCampanha, 'Cancelada'>, string>> = {
  Programada: 'Programada',
  'Em veiculação': 'Veiculando',
  Pausada: 'Veiculando',
  Encerrada: 'Concluída',
}

/** As frases da situação no portal (P-11), na ordem do status. */
export const SITUACOES_DA_CAMPANHA_NO_PORTAL = [
  'Ainda não começou', 'Em andamento', 'Pausada pela DigitalView', 'Concluída pela DigitalView',
] as const
export type SituacaoDaCampanhaNoPortal = (typeof SITUACOES_DA_CAMPANHA_NO_PORTAL)[number]

/** A situação no portal (P-11): o que a DigitalView registrou, sem afirmar o estado da plataforma de mídia. */
export const SITUACAO_DA_CAMPANHA_NO_PORTAL: Readonly<
  Record<Exclude<StatusGravadoDaCampanha, 'Cancelada'>, SituacaoDaCampanhaNoPortal>
> = {
  Programada: 'Ainda não começou',
  'Em veiculação': 'Em andamento',
  Pausada: 'Pausada pela DigitalView',
  Encerrada: 'Concluída pela DigitalView',
}

// ------------------------------------------------------------------------------------------------ os atos e o grafo

export const ATOS_DE_STATUS = ['iniciar', 'pausar', 'retomar', 'concluir', 'cancelar', 'reabrir_conclusao'] as const
export type AtoDeStatus = (typeof ATOS_DE_STATUS)[number]

/** De onde cada ato parte e aonde chega (§5.4). O job de abertura é o `iniciar` de quem chegou à data. */
export const TRANSICAO_DO_ATO: Readonly<Record<AtoDeStatus, { readonly de: readonly StatusGravadoDaCampanha[]; readonly para: StatusGravadoDaCampanha }>> = {
  iniciar: { de: ['Programada'], para: 'Em veiculação' },
  pausar: { de: ['Em veiculação'], para: 'Pausada' },
  retomar: { de: ['Pausada'], para: 'Em veiculação' },
  concluir: { de: ['Em veiculação', 'Pausada'], para: 'Encerrada' },
  cancelar: { de: ['Programada', 'Em veiculação', 'Pausada'], para: 'Cancelada' },
  reabrir_conclusao: { de: ['Encerrada'], para: 'Em veiculação' },
}

/** O nome de cada ato como o menu "Alterar status" o escreve. */
export const NOME_DO_ATO: Readonly<Record<AtoDeStatus, string>> = {
  iniciar: 'Iniciar',
  pausar: 'Pausar',
  retomar: 'Retomar',
  concluir: 'Concluir',
  cancelar: 'Cancelar',
  reabrir_conclusao: 'Reabrir conclusão',
}

/**
 * Quem pode cada ato (§5.6): `editar` é quem edita a campanha; `editar_e_excluir`, quem edita e tem o marcador
 * `excluir`; `editar_todos`, quem tem `midia · editar_todos` (a supervisão).
 */
export const PERMISSAO_DO_ATO: Readonly<Record<AtoDeStatus, 'editar' | 'editar_e_excluir' | 'editar_todos'>> = {
  iniciar: 'editar',
  pausar: 'editar',
  retomar: 'editar',
  concluir: 'editar',
  cancelar: 'editar_e_excluir',
  reabrir_conclusao: 'editar_todos',
}

export function atoPermitidoNoStatus(ato: AtoDeStatus, status: string): boolean {
  return (TRANSICAO_DO_ATO[ato].de as readonly string[]).includes(status)
}

/** Os atos que o status admite, na ordem do menu "Alterar status". */
export function atosDoStatus(status: string): readonly AtoDeStatus[] {
  return ATOS_DE_STATUS.filter((ato) => atoPermitidoNoStatus(ato, status))
}

/**
 * Uma aresta do grafo (P-02), como o gatilho do banco a julga: livre, só pelo Reabrir conclusão (a marca de
 * transação), proibida, ou nenhuma mudança (o mesmo status).
 */
export type ArestaDoGrafo = 'livre' | 'so_reabrindo' | 'proibida' | 'sem_mudanca'

export function arestaDoGrafo(de: StatusGravadoDaCampanha, para: StatusGravadoDaCampanha): ArestaDoGrafo {
  if (de === para) return 'sem_mudanca'
  const atos = ATOS_DE_STATUS.filter((ato) => TRANSICAO_DO_ATO[ato].para === para && atoPermitidoNoStatus(ato, de))
  if (atos.some((ato) => ato !== 'reabrir_conclusao')) return 'livre'
  return atos.length > 0 ? 'so_reabrindo' : 'proibida'
}

// ------------------------------------------------------------------------------------------------ a decisão do ato

export type RecusaDoAtoDeStatus =
  /** O status não admite o ato (Iniciar fora da Programada, Pausar fora da Em andamento, …). */
  | 'transicao_invalida'
  /** Concluir: a campanha já está concluída. */
  | 'ja_concluida'
  /** Concluir: a campanha está cancelada. */
  | 'cancelada'
  /** Concluir: a campanha ainda não começou. */
  | 'ainda_nao_iniciou'
  /** Cancelar: a campanha já está cancelada. */
  | 'ja_cancelada'
  /** Cancelar: a campanha concluída não se cancela (o faturamento tem fluxo próprio). */
  | 'concluida_nao_cancela'
  /** Iniciar com o início planejado no futuro: o início efetivo é obrigatório (P-03). */
  | 'inicio_efetivo_obrigatorio'
  /** Iniciar com o início planejado já alcançado: o ato não pede data, e uma data enviada seria ignorada em silêncio. */
  | 'inicio_efetivo_desnecessario'
  /** O início efetivo depois de hoje ou depois do fim (P-03). */
  | 'inicio_efetivo_fora_do_periodo'
  /** Concluir com data depois de hoje (P-04). */
  | 'conclusao_no_futuro'
  /** Concluir com data antes do início (P-04). */
  | 'conclusao_antes_do_inicio'
  /** Concluir sem a data efetiva. */
  | 'conclusao_sem_data'
  /** Reabrir conclusão sem motivo de 10 a 500 caracteres (P-06). */
  | 'motivo_obrigatorio'
  /** Pausar com motivo acima de 500 caracteres. */
  | 'motivo_longo'

export type CampoDoAtoDeStatus = 'status' | 'inicioEfetivo' | 'fimReal' | 'motivo'

export interface FatosDoAtoDeStatus {
  readonly ato: AtoDeStatus
  /** O status gravado, lido com a campanha travada. */
  readonly status: string
  readonly inicio: string
  readonly fim: string
  /** A data efetiva de conclusão gravada (a Encerrada tem). */
  readonly fimReal: string | null
  readonly hoje: string
  /** Iniciar: o início efetivo pedido quando o planejado é futuro (P-03). */
  readonly inicioEfetivo?: string | null | undefined
  /** Concluir: a data efetiva (P-04). */
  readonly dataDeConclusao?: string | null | undefined
  /** Pausar (opcional, até 500) e Reabrir conclusão (obrigatório, de 10 a 500). */
  readonly motivo?: string | null | undefined
}

/** O de/para de uma data que o ato muda, com a trilha em mente. */
export interface MudancaDeData {
  readonly de: string | null
  readonly para: string | null
}

export type ResultadoDoAtoDeStatus =
  | { readonly tipo: 'recusado'; readonly recusa: RecusaDoAtoDeStatus; readonly campo: CampoDoAtoDeStatus }
  | {
      readonly tipo: 'transicao'
      readonly de: StatusGravadoDaCampanha
      readonly para: StatusGravadoDaCampanha
      /** Iniciar com início futuro: o início planejado substituído pelo efetivo (P-03). */
      readonly inicio: (MudancaDeData & { readonly mudaDeMes: boolean }) | null
      /** Concluir grava a data; Reabrir conclusão a limpa (P-04, P-06). */
      readonly fimReal: MudancaDeData | null
      /** O motivo, aparado; nulo quando o ato não tem ou ele veio em branco. */
      readonly motivo: string | null
    }

/** Conta pontos de código, como o `char_length` do banco, e não unidades UTF-16. */
function tamanho(texto: string): number {
  return Array.from(texto).length
}

function recusa(motivo: RecusaDoAtoDeStatus, campo: CampoDoAtoDeStatus): ResultadoDoAtoDeStatus {
  return { tipo: 'recusado', recusa: motivo, campo }
}

/** O início efetivo cai noutro mês que o planejado: a tela avisa (P-03), porque o mês decide relatórios por período. */
export function inicioMudaDeMes(planejado: string, efetivo: string): boolean {
  return planejado.slice(0, 7) !== efetivo.slice(0, 7)
}

/** Iniciar pede o início efetivo quando o planejado ainda não chegou (P-03). */
export function iniciarPedeInicioEfetivo(inicio: string, hoje: string): boolean {
  return depoisDe(dataNominal(inicio), dataNominal(hoje))
}

/** A recusa de status de cada ato, para Concluir e Cancelar manterem os nomes de antes (a tela anterior os conhece). */
function recusaDeStatus(ato: AtoDeStatus, status: string): ResultadoDoAtoDeStatus {
  if (ato === 'concluir') {
    if (status === 'Encerrada') return recusa('ja_concluida', 'status')
    if (status === 'Cancelada') return recusa('cancelada', 'status')
    if (status === 'Programada') return recusa('ainda_nao_iniciou', 'status')
  }
  if (ato === 'cancelar') {
    if (status === 'Cancelada') return recusa('ja_cancelada', 'status')
    if (status === 'Encerrada') return recusa('concluida_nao_cancela', 'status')
  }
  return recusa('transicao_invalida', 'status')
}

export function decidirAtoDeStatus(f: FatosDoAtoDeStatus): ResultadoDoAtoDeStatus {
  if (!ehStatusGravadoDaCampanha(f.status) || !atoPermitidoNoStatus(f.ato, f.status)) return recusaDeStatus(f.ato, f.status)
  const de = f.status
  const para = TRANSICAO_DO_ATO[f.ato].para
  const hoje = dataNominal(f.hoje)
  const motivoAparado = f.motivo === null || f.motivo === undefined ? '' : f.motivo.trim()
  const semData = { tipo: 'transicao', de, para, inicio: null, fimReal: null, motivo: null } as const

  switch (f.ato) {
    case 'iniciar': {
      const efetivo = f.inicioEfetivo ?? null
      if (!iniciarPedeInicioEfetivo(f.inicio, f.hoje)) {
        return efetivo === null ? semData : recusa('inicio_efetivo_desnecessario', 'inicioEfetivo')
      }
      if (efetivo === null) return recusa('inicio_efetivo_obrigatorio', 'inicioEfetivo')
      const data = dataNominal(efetivo)
      if (depoisDe(data, hoje) || depoisDe(data, dataNominal(f.fim))) {
        return recusa('inicio_efetivo_fora_do_periodo', 'inicioEfetivo')
      }
      return { ...semData, inicio: { de: f.inicio, para: efetivo, mudaDeMes: inicioMudaDeMes(f.inicio, efetivo) } }
    }
    case 'concluir': {
      const data = f.dataDeConclusao ?? null
      if (data === null) return recusa('conclusao_sem_data', 'fimReal')
      if (depoisDe(dataNominal(data), hoje)) return recusa('conclusao_no_futuro', 'fimReal')
      if (antesDe(dataNominal(data), dataNominal(f.inicio))) return recusa('conclusao_antes_do_inicio', 'fimReal')
      return { ...semData, fimReal: { de: f.fimReal, para: data } }
    }
    case 'pausar':
      if (tamanho(motivoAparado) > 500) return recusa('motivo_longo', 'motivo')
      return { ...semData, motivo: motivoAparado === '' ? null : motivoAparado }
    case 'reabrir_conclusao':
      if (tamanho(motivoAparado) < 10 || tamanho(motivoAparado) > 500) return recusa('motivo_obrigatorio', 'motivo')
      return { ...semData, fimReal: { de: f.fimReal, para: null }, motivo: motivoAparado }
    case 'retomar':
    case 'cancelar':
      return semData
  }
}

/** "A", "A ou B", "A, B ou C". */
function listaNaFrase(itens: readonly string[]): string {
  return itens.length <= 1 ? (itens[0] ?? '') : `${itens.slice(0, -1).join(', ')} ou ${itens.at(-1) ?? ''}`
}

/** A frase de cada recusa, com o status pelo mapa único (P-01). */
export function mensagemDaRecusaDoAtoDeStatus(
  recusaDoAto: RecusaDoAtoDeStatus,
  fatos: { readonly ato: AtoDeStatus; readonly status: string; readonly inicio: string },
): string {
  const atual = rotuloDoStatusDaCampanha(fatos.status)
  switch (recusaDoAto) {
    case 'transicao_invalida': {
      const de = listaNaFrase(TRANSICAO_DO_ATO[fatos.ato].de.map(rotuloDoStatusDaCampanha))
      return `${NOME_DO_ATO[fatos.ato]} vale só para campanha ${de}. Esta campanha está ${atual}.`
    }
    case 'ja_concluida':
      return `Esta campanha já está ${atual}.`
    case 'cancelada':
      return `Uma campanha ${rotuloDoStatusDaCampanha('Cancelada')} não se conclui.`
    case 'ainda_nao_iniciou':
      return `Só uma campanha ${rotuloDoStatusDaCampanha('Em veiculação')} ou ${rotuloDoStatusDaCampanha('Pausada')} pode ser concluída. Esta campanha está ${atual}.`
    case 'ja_cancelada':
      return `Esta campanha já está ${atual}.`
    case 'concluida_nao_cancela':
      return `A campanha está ${atual}; corrija o faturamento pelo fluxo correspondente.`
    case 'inicio_efetivo_obrigatorio':
      return 'O início planejado ainda não chegou. Informe o início efetivo: ele substitui o planejado.'
    case 'inicio_efetivo_desnecessario':
      return 'O início planejado já chegou: a campanha começa sem trocar o início. Inicie sem informar o início efetivo.'
    case 'inicio_efetivo_fora_do_periodo':
      return 'O início efetivo precisa ser até hoje e até o fim da veiculação.'
    case 'conclusao_sem_data':
      return 'Informe a data efetiva de conclusão.'
    case 'conclusao_no_futuro':
      return 'Concluir é declarar que a veiculação já acabou: a data não pode estar no futuro.'
    case 'conclusao_antes_do_inicio':
      return 'A data efetiva de conclusão precisa estar entre o início da veiculação e hoje.'
    case 'motivo_obrigatorio':
      return 'O motivo precisa ter de 10 a 500 caracteres.'
    case 'motivo_longo':
      return 'O motivo pode ter até 500 caracteres.'
  }
}

// ------------------------------------------------------------------------------------------------ a edição por status

/**
 * O que se edita numa campanha, linha a linha da tabela do §5.5 (P-10). As linhas de vínculo, links e portal valem
 * desde já, para as etapas que os trazem lerem a mesma tabela.
 */
export const EDICOES_DA_CAMPANHA = [
  'composicao',
  'alterar_valor',
  'reclassificar',
  'adotar_meta_padrao',
  'registrar_gasto',
  'fonte_do_gasto',
  'vincular_campaign',
  'desvincular',
  'manutencao_dv360',
  'links',
  'ler_e_excluir_links',
  'ligar_portal',
  'desligar_portal',
  'nome',
  'responsaveis',
  'datas',
  'arquivar',
] as const
export type EdicaoDaCampanha = (typeof EDICOES_DA_CAMPANHA)[number]

const ATE_CONCLUIDA = ['Programada', 'Em veiculação', 'Pausada', 'Encerrada'] as const satisfies readonly StatusGravadoDaCampanha[]
const ANTES_DA_CONCLUSAO = ['Programada', 'Em veiculação', 'Pausada'] as const satisfies readonly StatusGravadoDaCampanha[]

/**
 * A tabela ÚNICA da edição por status (§5.5, P-10): os status em que cada coisa se edita.
 *
 * - Composição: até a Pausada; na Concluída só depois de Reabrir conclusão; nunca na Cancelada.
 * - Reclassificar: até a Concluída, onde é só etiqueta (adotar a meta padrão não existe nela).
 * - Registrar gasto, Fonte do gasto, Vincular Campaign, Links e ligar o portal: até a Concluída.
 * - Desvincular, a manutenção do DV360 (Ampliar histórico, Tentar de novo, Retomar acompanhamento, Reconferir,
 *   Encerrar coleta), ler e excluir links, desligar o portal e arquivar: em todos (arquivar exige, na etapa do DV360,
 *   não ter vínculo ativo).
 * - Nome e responsáveis: até a Concluída. Datas: até a Pausada, e na Em andamento e na Pausada com início ≤ hoje
 *   (`INICIO_ATE_HOJE`).
 */
export const EDICAO_POR_STATUS: Readonly<Record<EdicaoDaCampanha, readonly StatusGravadoDaCampanha[]>> = {
  composicao: ANTES_DA_CONCLUSAO,
  alterar_valor: ATE_CONCLUIDA,
  reclassificar: ATE_CONCLUIDA,
  adotar_meta_padrao: ANTES_DA_CONCLUSAO,
  registrar_gasto: ATE_CONCLUIDA,
  fonte_do_gasto: ATE_CONCLUIDA,
  vincular_campaign: ATE_CONCLUIDA,
  desvincular: STATUS_GRAVADOS_DA_CAMPANHA,
  manutencao_dv360: STATUS_GRAVADOS_DA_CAMPANHA,
  links: ATE_CONCLUIDA,
  ler_e_excluir_links: STATUS_GRAVADOS_DA_CAMPANHA,
  ligar_portal: ATE_CONCLUIDA,
  desligar_portal: STATUS_GRAVADOS_DA_CAMPANHA,
  nome: ATE_CONCLUIDA,
  responsaveis: ATE_CONCLUIDA,
  datas: ANTES_DA_CONCLUSAO,
  arquivar: STATUS_GRAVADOS_DA_CAMPANHA,
}

/** Os status que já começaram: o início deles não vai para depois de hoje (P-09; o CHECK do banco também). */
export const INICIO_ATE_HOJE: readonly StatusGravadoDaCampanha[] = ['Em veiculação', 'Pausada']

export function permiteNoStatus(edicao: EdicaoDaCampanha, status: string): boolean {
  return (EDICAO_POR_STATUS[edicao] as readonly string[]).includes(status)
}

// ------------------------------------------------------------------------------------------------ o PATCH (P-09)

export type RecusaDaEdicaoDaCampanha =
  /** Nada se edita na Cancelada (§5.5). */
  | 'cancelada_nao_edita'
  /** Na Concluída, só nome e responsáveis: as datas mudam depois de Reabrir conclusão (§5.5). */
  | 'datas_na_concluida'
  /**
   * O campo não se edita neste status pela tabela única, fora dos dois casos acima (DS-656 (f)). Hoje só o status fora do
   * vocabulário chega aqui; um ajuste futuro na tabela também, sem o PATCH e as capacidades divergirem (P-10).
   */
  | 'edicao_fechada_no_status'
  /** Em andamento e Pausada já começaram: o início não vai para depois de hoje (P-09). */
  | 'inicio_futuro_depois_de_iniciada'

export interface FatosDaEdicaoDaCampanha {
  readonly status: string
  readonly hoje: string
  /** O que o corpo do PATCH muda: o nome, os responsáveis e as datas (o novo início, quando vem). */
  readonly mudaNome: boolean
  readonly mudaResponsaveis: boolean
  readonly inicioNovo: string | null
  readonly mudaFim: boolean
}

export function decidirEdicaoDaCampanha(
  f: FatosDaEdicaoDaCampanha,
): { readonly recusa: RecusaDaEdicaoDaCampanha; readonly campo: 'nome' | 'responsavelIds' | 'inicio' | 'fim' } | null {
  const mudaDatas = f.inicioNovo !== null || f.mudaFim
  const campoDasDatas = f.inicioNovo !== null ? 'inicio' : 'fim'
  /*
    P-10 (E5R-M2): cada campo que muda passa pela SUA linha da tabela única, as mesmas linhas que as capacidades
    `podeEditarNome`, `podeEditarResponsaveis` e `podeEditarDatas` leem. Um ajuste na tabela muda o PATCH e a tela
    juntos, e o status fora do vocabulário não edita nada. A ordem do campo nomeado é a de sempre: nome, datas,
    responsáveis.
  */
  const fechado = f.mudaNome && !permiteNoStatus('nome', f.status)
    ? 'nome'
    : mudaDatas && !permiteNoStatus('datas', f.status)
      ? campoDasDatas
      : f.mudaResponsaveis && !permiteNoStatus('responsaveis', f.status) ? 'responsavelIds' : null
  if (fechado !== null) {
    // O código diz o porquê: a Cancelada não muda mais; a Concluída muda as datas depois de Reabrir conclusão.
    const recusa: RecusaDaEdicaoDaCampanha = f.status === 'Cancelada'
      ? 'cancelada_nao_edita'
      : f.status === 'Encerrada' && (fechado === 'inicio' || fechado === 'fim') ? 'datas_na_concluida' : 'edicao_fechada_no_status'
    return { recusa, campo: fechado }
  }
  if (f.inicioNovo !== null && (INICIO_ATE_HOJE as readonly string[]).includes(f.status)
    && depoisDe(dataNominal(f.inicioNovo), dataNominal(f.hoje))) {
    return { recusa: 'inicio_futuro_depois_de_iniciada', campo: 'inicio' }
  }
  return null
}

export function mensagemDaRecusaDaEdicao(recusaDaEdicao: RecusaDaEdicaoDaCampanha, status: string): string {
  const atual = rotuloDoStatusDaCampanha(status)
  switch (recusaDaEdicao) {
    case 'cancelada_nao_edita':
      return `Uma campanha ${atual} não muda mais: nome, datas e responsáveis ficam como estão.`
    case 'datas_na_concluida':
      // O texto do protótipo aprovado da 5.2 (`#ecDatasTrava`): o rótulo dentro da frase, em minúscula (E5R-M3).
      return `Campanha ${rotuloDoStatusNaFrase(status)}: o período não muda mais. Para corrigir as datas, é preciso Reabrir conclusão.`
    case 'edicao_fechada_no_status':
      return `Uma campanha ${atual} não admite essa edição.`
    case 'inicio_futuro_depois_de_iniciada':
      return `Uma campanha ${atual} já começou: o início não pode ficar depois de hoje.`
  }
}
